import * as React from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";

import type { DuprSsoConfigDto } from "@volley/data";

import fetchApi, { logFetchError } from "../../../util/fetchApi";
import DuprConnect from "../../Account/DuprConnect";
import CloseableDialogTitle from "../../common/CloseableDialogTitle";

export interface DuprConnectLaunch {
    /**
     * Open the DUPR connect flow for the "Connect DUPR" option (see
     * `isDuprConnectOption`). Bypasses the chat entirely, mirroring how the
     * assessment-launch options drive the assessment flow directly.
     */
    launchDuprConnect: () => void;
    /** The DUPR connect dialog element to render. */
    dialog: React.JSX.Element;
}

/**
 * Bridges the Ask Volley profile prompt to the existing DUPR linking machinery
 * so the "Connect DUPR" option skips the chat and opens the same DuprConnect SSO
 * flow used on the Account screen — yielding a verified rating the rest of the
 * app already consumes, rather than an unverified self-reported number. Owns the
 * hosting dialog and lazily fetches the (non-secret) SSO config the first time
 * the user opts in.
 */
export function useDuprConnect(options?: {
    /**
     * Called whenever the connect dialog closes. The SSO link happens inside
     * `DuprConnect`, which doesn't notify its parent, so hosts use this to
     * re-fetch the (possibly newly linked) DUPR profile.
     */
    onClose?: () => void;
}): DuprConnectLaunch {
    const [open, setOpen] = React.useState(false);
    const [ssoConfig, setSsoConfig] = React.useState<DuprSsoConfigDto | null>(
        null,
    );
    // Fetch the config at most once, and only after the user opts in — the
    // profile prompt is just one of many, so we don't pay for it up front.
    const requested = React.useRef(false);

    const launchDuprConnect = React.useCallback(() => {
        setOpen(true);
        if (requested.current) return;
        requested.current = true;
        fetchApi<DuprSsoConfigDto>("/api/integrations/dupr/config")
            .then(setSsoConfig)
            .catch(logFetchError);
    }, []);

    const onClose = options?.onClose;
    const handleClose = React.useCallback(() => {
        setOpen(false);
        onClose?.();
    }, [onClose]);

    const dialog = (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <CloseableDialogTitle onClose={handleClose}>
                Connect your DUPR® rating
            </CloseableDialogTitle>
            <DialogContent dividers>
                {!ssoConfig ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                ) : ssoConfig.enabled ? (
                    <DuprConnect ssoConfig={ssoConfig} />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        DUPR connection isn&apos;t available right now.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );

    return { launchDuprConnect, dialog };
}
